import { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes, faSave } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch } from '../../utils/http';
import AdminStudentDocumentsEditor from './AdminStudentDocumentsEditor';
import type { AdminDoc } from './AdminStudentDocumentsEditor';
import PasswordResetBox from './PasswordResetBox';

interface StudentDetail {
  userId: string;
  studentRecordId: string;
  studentId: string;
  firstName: string;
  lastName: string;
  email: string;
  isActive: boolean;
  phoneNumber: string;
  dateOfBirth: string | null;
  address: string;
  currentLearnership: string;
  status: number;
  documents: AdminDoc[];
}

interface Props {
  userId: string;
  onClose: () => void;
  onSaved: () => void;
}

type Tab = 'details' | 'documents' | 'security';

const inputClass = 'w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500';

/** Admin editor for a student: account details, status, documents and password. */
export default function EditStudentModal({ userId, onClose, onSaved }: Props) {
  const { accessToken } = useAuth();
  const [tab, setTab] = useState<Tab>('details');
  const [data, setData] = useState<StudentDetail | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    isActive: true,
    phoneNumber: '',
    dateOfBirth: '',
    address: '',
    currentLearnership: '',
    status: 0,
  });

  const load = useCallback(
    async (resetForm: boolean) => {
      const res = await authFetch<StudentDetail>(accessToken, `/api/admin/users/students/${userId}`);
      if (res.ok && res.data) {
        setData(res.data);
        if (resetForm) {
          setForm({
            firstName: res.data.firstName,
            lastName: res.data.lastName,
            email: res.data.email,
            isActive: res.data.isActive,
            phoneNumber: res.data.phoneNumber ?? '',
            dateOfBirth: res.data.dateOfBirth ? res.data.dateOfBirth.slice(0, 10) : '',
            address: res.data.address ?? '',
            currentLearnership: res.data.currentLearnership ?? '',
            status: res.data.status,
          });
        }
      } else {
        setError(res.message || 'Failed to load the student.');
      }
    },
    [accessToken, userId]
  );

  useEffect(() => {
    load(true);
  }, [load]);

  const set = (name: string, value: string | boolean | number) => {
    setNotice('');
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const save = async () => {
    setError('');
    setNotice('');
    setIsSaving(true);
    const res = await authFetch(accessToken, `/api/admin/users/students/${userId}`, {
      method: 'PUT',
      body: { ...form, dateOfBirth: form.dateOfBirth || null },
    });
    setIsSaving(false);
    if (!res.ok) {
      setError(res.message);
      return;
    }
    setNotice('Student updated.');
    onSaved();
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'details', label: 'Details' },
    { id: 'documents', label: 'Documents' },
    { id: 'security', label: 'Password' },
  ];

  return (
    <div className="fixed inset-0 bg-brand-950/40 z-50 flex items-start justify-center px-4 py-8 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 my-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-brand-950">Edit student</h3>
            {data && <p className="text-xs text-brand-900/50">{data.studentId}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-canvas-100 rounded-lg text-brand-900/60">
            <FontAwesomeIcon icon={faTimes} className="h-4 w-4" />
          </button>
        </div>

        {!data && !error && (
          <div className="flex justify-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-brand-600 animate-spin" />
          </div>
        )}

        {data && (
          <>
            <div className="flex gap-1 border-b border-canvas-300 mb-5 overflow-x-auto">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-4 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap transition-all ${
                    tab === t.id ? 'border-brand-600 text-brand-700' : 'border-transparent text-brand-900/60 hover:text-brand-900'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'details' && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">First name</label>
                    <input className={inputClass} value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Last name</label>
                    <input className={inputClass} value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Email (used to sign in)</label>
                  <input type="email" className={inputClass} value={form.email} onChange={(e) => set('email', e.target.value)} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Phone</label>
                    <input className={inputClass} value={form.phoneNumber} onChange={(e) => set('phoneNumber', e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Date of birth</label>
                    <input type="date" className={inputClass} value={form.dateOfBirth} onChange={(e) => set('dateOfBirth', e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Address</label>
                  <textarea rows={2} className={`${inputClass} resize-none`} value={form.address} onChange={(e) => set('address', e.target.value)} />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-950 mb-2">Current learnership (label)</label>
                  <input className={inputClass} value={form.currentLearnership} onChange={(e) => set('currentLearnership', e.target.value)} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Student status</label>
                    <select className={`${inputClass} bg-white`} value={form.status} onChange={(e) => set('status', parseInt(e.target.value, 10))}>
                      <option value={0}>Active</option>
                      <option value={1}>Inactive</option>
                      <option value={2}>Suspended</option>
                      <option value={3}>Graduated</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-brand-950 mb-2">Can sign in</label>
                    <label className="flex items-center gap-2 text-sm text-brand-900/70 h-10">
                      <input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
                      Account enabled
                    </label>
                  </div>
                </div>

                {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}
                {notice && <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">{notice}</p>}

                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={onClose} className="px-5 py-2 border border-canvas-300 text-brand-950 font-semibold rounded-lg hover:bg-canvas-100 transition-colors text-sm">
                    Close
                  </button>
                  <button
                    onClick={save}
                    disabled={isSaving}
                    className="px-5 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={isSaving ? faSpinner : faSave} className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
                    Save changes
                  </button>
                </div>
              </div>
            )}

            {tab === 'documents' && (
              <AdminStudentDocumentsEditor studentRecordId={data.studentRecordId} documents={data.documents} onChanged={() => load(false)} />
            )}

            {tab === 'security' && <PasswordResetBox userId={data.userId} />}
          </>
        )}

        {!data && error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</p>}
      </div>
    </div>
  );
}
